import { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../Styles/Brands.module.css';
import Container from '../UI/Container';
import SectionTitle from '../UI/SectionTitle';

// ── Logos con rutas exactas del proyecto ──
import dahua     from '../../Assets/dahua.png';
import lanpro    from '../../Assets/lanpro.png';
import ruijie    from '../../Assets/ruijie.png';
import hikvision from '../../Assets/hikvision.png';
import lps       from '../../Assets/lps.jpg';
import tplink    from '../../Assets/tplink.png';
import imou      from '../../Assets/imou.png';
import ezviz     from '../../Assets/ezviz.png';
import tapo      from '../../Assets/tapo.webp';
import ajax      from '../../Assets/ajax.png';
import intelbras from '../../Assets/intelbras.png';
import hagroy    from '../../Assets/hagroy.png';
import microtik  from '../../Assets/mikrotik.webp';
import huawei    from '../../Assets/huawei.png';
import vsol      from '../../Assets/vsol.webp';
import panduit   from '../../Assets/panduit.webp';
import siemon    from '../../Assets/siemon.webp';
import witek     from '../../Assets/witek.png';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const brandGroups = [
  [
    { name: 'Dahua',     logo: dahua,     alt: 'Dahua Technology' },
    { name: 'Lanpro',    logo: lanpro,    alt: 'Lanpro' },
    { name: 'Ruijie',    logo: ruijie,    alt: 'Ruijie' },
  ],
  [
    { name: 'Hikvision', logo: hikvision, alt: 'Hikvision' },
    { name: 'LPS',       logo: lps,       alt: 'LPS' },
    { name: 'TP-Link',   logo: tplink,    alt: 'TP-Link' },
  ],
  [
    { name: 'Imou',      logo: imou,      alt: 'Imou' },
    { name: 'Ezviz',     logo: ezviz,     alt: 'Ezviz' },
    { name: 'Tapo',      logo: tapo,      alt: 'Tapo' },
  ],
  [
    { name: 'Ajax',      logo: ajax,      alt: 'Ajax Systems' },
    { name: 'Intelbras', logo: intelbras, alt: 'Intelbras' },
    { name: 'Hagroy',    logo: hagroy,    alt: 'Hagroy' },
  ],
  [
    { name: 'Microtik',  logo: microtik,  alt: 'MikroTik' },
    { name: 'Huawei',    logo: huawei,    alt: 'Huawei' },
    { name: 'Vsol',      logo: vsol,      alt: 'Vsol' },
  ],
  [
    { name: 'Panduit',   logo: panduit,   alt: 'Panduit' },
    { name: 'Siemon',    logo: siemon,    alt: 'Siemon' },
    { name: 'Witek',     logo: witek,     alt: 'Witek' },
  ],
];

const TOTAL_GROUPS  = brandGroups.length;
const BRAND_DELAY   = 2000;
const GROUP_DELAY   = 4000;
const FADE_DURATION = 350; // ms — debe coincidir con transition en CSS

const Brands = () => {
  const [currentGroup, setCurrentGroup]   = useState(0);
  const [currentBrand, setCurrentBrand]   = useState(0);
  const [visibleGroup, setVisibleGroup]   = useState(0);
  const [visibleBrand, setVisibleBrand]   = useState(0);
  const [isVisible, setIsVisible]         = useState(true);
  const [isPaused, setIsPaused]           = useState(false);
  const [isMobile, setIsMobile]           = useState(false);
  const brandTimerRef                     = useRef(null);

  // ── Detectar móvil ──
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Transición suave: fade out → actualiza contenido → fade in ──
  const transitionTo = useCallback((newGroup, newBrand = 0) => {
    setIsVisible(false);
    setTimeout(() => {
      setVisibleGroup(newGroup);
      setVisibleBrand(newBrand);
      setIsVisible(true);
    }, FADE_DURATION);
  }, []);

  // ── Navegación de grupos ──
  const goToGroup = useCallback((index) => {
    setCurrentGroup(index);
    setCurrentBrand(0);
    transitionTo(index, 0);
  }, [transitionTo]);

  const nextGroup = useCallback(() => {
    const next = (currentGroup + 1) % TOTAL_GROUPS;
    goToGroup(next);
  }, [currentGroup, goToGroup]);

  const prevGroup = () => {
    const prev = (currentGroup - 1 + TOTAL_GROUPS) % TOTAL_GROUPS;
    goToGroup(prev);
  };

  // ── Autoplay desktop ──
  useEffect(() => {
    if (isMobile || isPaused) return;
    const timer = setInterval(nextGroup, GROUP_DELAY);
    return () => clearInterval(timer);
  }, [isMobile, isPaused, nextGroup]);

  // ── Autoplay móvil ──
  useEffect(() => {
    if (!isMobile || isPaused) return;

    clearInterval(brandTimerRef.current);
    brandTimerRef.current = setInterval(() => {
      setCurrentBrand((prevBrand) => {
        const nextBrand = prevBrand + 1;
        if (nextBrand >= brandGroups[currentGroup].length) {
          const nextGroup = (currentGroup + 1) % TOTAL_GROUPS;
          setCurrentGroup(nextGroup);
          transitionTo(nextGroup, 0);
          return 0;
        }
        transitionTo(currentGroup, nextBrand);
        return nextBrand;
      });
    }, BRAND_DELAY);

    return () => clearInterval(brandTimerRef.current);
  }, [isMobile, isPaused, currentGroup, transitionTo]);

  // Navegar marca manualmente en móvil
  const goToBrand = (i) => {
    setCurrentBrand(i);
    transitionTo(currentGroup, i);
  };

  return (
    <section className={styles.brands}>

      <div className={styles.orbCenter} />

      <Container>
        <SectionTitle
          title="Tecnología de calidad"
          subtitle="Marcas líderes que garantizan confiabilidad y rendimiento"
          highlight="calidad"
          light={true}
        />

        <div
          className={styles.carousel}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <button className={styles.arrowBtn} onClick={prevGroup} aria-label="Grupo anterior">
            <ChevronLeft />
          </button>

          {/* ── DESKTOP: 3 cards ── */}
          {!isMobile && (
            <div
              className={styles.grid}
              style={{
                opacity:    isVisible ? 1 : 0,
                transform:  isVisible ? 'scale(1)' : 'scale(0.96)',
                transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
              }}
            >
              {brandGroups[visibleGroup].map((brand) => (
                <div key={brand.name} className={styles.brandCard}>
                  <img src={brand.logo} alt={brand.alt} className={styles.brandLogo} />
                </div>
              ))}
            </div>
          )}

          {/* ── MÓVIL: 1 card ── */}
          {isMobile && (
            <div className={styles.mobileCardWrap}>
              <div
                className={styles.brandCard}
                style={{
                  opacity:    isVisible ? 1 : 0,
                  transform:  isVisible ? 'scale(1)' : 'scale(0.96)',
                  transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
                }}
              >
                <img
                  src={brandGroups[visibleGroup][visibleBrand].logo}
                  alt={brandGroups[visibleGroup][visibleBrand].alt}
                  className={styles.brandLogo}
                />
              </div>

              <div className={styles.groupDots}>
                {brandGroups[currentGroup].map((_, i) => (
                  <button
                    key={i}
                    className={`${styles.groupDot} ${i === currentBrand ? styles.groupDotActive : ''}`}
                    onClick={() => goToBrand(i)}
                    aria-label={`Ver ${brandGroups[currentGroup][i].name}`}
                  />
                ))}
              </div>
            </div>
          )}

          <button className={styles.arrowBtn} onClick={nextGroup} aria-label="Grupo siguiente">
            <ChevronRight />
          </button>
        </div>

        {/* ── Dots de grupo ── */}
        <div className={styles.dots}>
          {brandGroups.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === currentGroup ? styles.dotActive : ''}`}
              onClick={() => goToGroup(index)}
              aria-label={`Ir al grupo ${index + 1}`}
            />
          ))}
        </div>

        <div className={styles.quoteWrapper}>
          <div className={styles.quoteLine} />
          <p className={styles.quote}>
            Soluciones confiables para empresas y hogares
          </p>
        </div>

      </Container>
    </section>
  );
};

export default Brands;